import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { findIndex, get, isEmpty, isEqual, set, toNumber } from 'lodash';

import { showToast } from '@repo/ui';
import { StableQueryKey, useQueryKeyStore } from '@xtnd/web/shared/stores';

import { arMatterApi, arCollectionQueueApi, reportingAppealsApi } from '../api';
import {
  ArCollectionQueueParams,
  ArMatter,
  ArMatterResponse,
  FileCategory,
  MarkToBeAppealedPayload,
  MarkWriteOffPayload,
  MatterInvoice,
  MatterInvoiceSummaryFile,
  UpdateAppealStatusPayload,
} from '../types';
import { AppealStatus, AppealType } from '@repo/types';
import { ArMatterInvoiceSummaryFilesResponse } from '../types';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

export const useGetMatters = (queryParams: ArCollectionQueueParams) => {
  const groupBy = queryParams.filters?.groupBy;
  const entityId = queryParams.filters?.entityId;
  const clientId = queryParams.filters?.clientId;

  return useQuery({
    queryKey: ['matters', groupBy, entityId, clientId] as const,
    enabled: entityId !== undefined,
    queryFn: () => arCollectionQueueApi.getMatters(queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

export const useMatterDetailQuery = (
  matterId: string | null,
  clientId: string | null,
  daysPastDue: number | null,
  daysPastDueCap?: number | null
) => {
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  // Coerce both nullable scalar args to `null` for queryKey stability so a caller
  // passing `undefined` doesn't generate a separate cache entry from `null`.
  const queryKey = ['matters', matterId, clientId, daysPastDue ?? null, daysPastDueCap ?? null] as const;
  const queryKeyStore = getQueryKey('ar-collections', 'queue.matters');
  if (!isEqual(queryKeyStore, queryKey)) {
    setQueryKey('ar-collections', 'queue.matters', queryKey as any);
  }
  return useQuery({
    queryKey: queryKey,
    queryFn: () =>
      arMatterApi.getMatterById(matterId || '', clientId || '', daysPastDue, daysPastDueCap),
    enabled: !!matterId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useMatterInvoicesQuery = (
  matterId: string | null,
  clientId: string | null,
  daysPastDue: number | null,
  daysPastDueCap?: number | null
) => {
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  // Coerce both nullable scalar args to `null` for queryKey stability.
  const queryKey = [
    'matter',
    'invoices',
    matterId,
    clientId,
    daysPastDue ?? null,
    daysPastDueCap ?? null,
  ];
  const queryKeyStore = getQueryKey('ar-collections', 'queue.matter-invoices');
  if (!isEqual(queryKeyStore, queryKey)) {
    setQueryKey('ar-collections', 'queue.matter-invoices', queryKey as StableQueryKey);
  }
  return useQuery({
    queryKey,
    queryFn: () =>
      arMatterApi.getMatterInvoices(matterId || '', clientId || '', daysPastDue, daysPastDueCap),
    enabled: !!matterId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useMatterInvoiceSummaryFilesQuery = (
  invoiceId: string,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: ['matter', 'invoice', 'summary-files', invoiceId] as const,
    queryFn: () => arMatterApi.getMatterInvoiceSummaryFiles(invoiceId),
    enabled: options.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useMatterAttemptsQuery = (matterId: string | null, clientId: string | null) => {
  return useQuery({
    queryKey: ['matter', 'attempts', matterId, clientId] as const,
    queryFn: async () => {
      const response = await arMatterApi.getMatterAttempts(matterId || '', clientId || '');
      const sortedAttempts = (response.data || []).sort(
        (a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
      );
      return { ...response, attempts: sortedAttempts };
    },
    enabled: !!matterId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useAddMatterAttemptLog = (matterId: string | null, clientId: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) =>
      arMatterApi.postMatterAttempt(matterId || '', clientId || '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['matter', 'attempts', matterId, clientId],
      });
    },
    onError: error => {
      showToast.error(getApiErrorMessage(error, 'Failed to add attempt log. Please try again.'));
    },
  });
};

export const useUpdateMatterInvoiceCarrierInPreview = () => {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const matterKey = getQueryKey('ar-collections', 'queue.matters');
  const templateEmailKey = getQueryKey('ar-collections', 'reporting.email-queue-template');

  return useMutation({
    mutationFn: (payload: { invoiceId: string; isCarrierInPreview: boolean }) =>
      arMatterApi.updateMatterInvoiceCarrierInPreview(payload),
    onSuccess: (response: any) => {
      // Update the specific invoice in the cache
      const matterInvoiceQuery = queryClient.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];

      const invoiceIndex = findIndex(
        matterInvoices,
        invoice => invoice.invoiceId === response.invoiceId
      );

      if (invoiceIndex !== -1) {
        set(matterInvoices, `[${invoiceIndex}].isCarrierInPreview`, response.isCarrierInPreview);
        queryClient.setQueryData(matterInvoicesQueryKey, {
          ...matterInvoiceQuery,
          data: matterInvoices,
        });
        if (templateEmailKey) {
          queryClient.removeQueries({
            queryKey: templateEmailKey,
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: templateEmailKey,
          });
        }
      }
      showToast.success('Update Matter Invoice CarrierInPreview Success');
    },
    onError: error => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update carrier preview status. Please try again.')
      );
    },
  });
};

export const useMarkToBeAppealed = (invoiceId: string) => {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');

  return useMutation({
    mutationFn: (payload: MarkToBeAppealedPayload) => {
      const payloadTransform: UpdateAppealStatusPayload = {
        invoiceId,
        nextStatus: AppealStatus.TO_BE_APPEALED,
        comment: payload.formData.comment,
        remainingAmount: toNumber(payload.formData.remainingAmount),
        appealType: payload.appealType,
        deadline: payload.formData.deadline,
        attyDeadline: payload.formData.attyDeadline,
        feesCut: toNumber(payload.formData.feesCut),
        expensesCut: toNumber(payload.formData.expensesCut),
        promptPayment: toNumber(payload.formData.promptPayment),
        bulkDiscount: toNumber(payload.formData.bulkDiscount),
        administrativeFee: toNumber(payload.formData.administrativeFee),
        timekeepers: payload.formData.timekeepers,
        files: payload.formData.files,
        includeCreditsIssued: payload.formData.includeCreditsIssued,
        appealedFees: toNumber(payload.formData.appealedFees),
        appealedExpenses: toNumber(payload.formData.appealedExpenses),
      };
      return reportingAppealsApi.updateAppealStatus(payloadTransform);
    },
    onSuccess: (response: any, _payload) => {
      const matterInvoiceQuery = queryClient.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];
      const invoice = response.data;

      const invoiceIndex = findIndex(
        matterInvoices,
        matterInvoice => matterInvoice.invoiceId === invoice.invoiceId
      );

      const appeal = get(invoice, 'status', AppealStatus.TO_BE_APPEALED);

      if (invoiceIndex !== -1) {
        const collectedAmount = invoice.collectedAmount ?? 0;
        const paid = invoice.paid ?? 0;
        const arBalance = Math.round((collectedAmount - paid) * 100) / 100;

        const updatedInvoices = matterInvoices.map((inv, idx) =>
          idx === invoiceIndex
            ? {
                ...inv,
                appealStatus: appeal,
                isCarrierInPreview: true,
                remainingAmount: invoice.remainingAmount,
                collectedAmount: invoice.collectedAmount,
                appealComments: invoice.comments,
                writeOffAmount: invoice.writeOffAmount,
                actualBalance: invoice.actualBalance,
                arBalance,
              }
            : inv
        );

        queryClient.setQueryData(matterInvoicesQueryKey, {
          ...matterInvoiceQuery,
          data: updatedInvoices,
        });
      } else {
        queryClient.setQueryData(matterInvoicesQueryKey, {
          ...matterInvoiceQuery,
          data: matterInvoices,
        });
      }

      // Invalidate the appeals and appeal by client id query keys
      const invalidateQueries = [appealsQueryKey, appealByClientIdQueryKey];
      invalidateQueries.forEach(queryKey => {
        if (!isEmpty(queryKey)) {
          queryClient.invalidateQueries({
            queryKey,
          });
        }
      });

      const toastMessage = _payload.appealType === AppealType.BACKLOG
        ? `Marked as 'Appeal Submitted (Backlog)' successfully.`
        : `Marked as 'To Be Appealed' successfully.`;
      showToast.success(toastMessage);
    },
    onError: error => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update appeal status. Please try again.')
      );
    },
  });
};

export const useMarkPartialWriteOff = () => {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');
  return useMutation({
    mutationFn: (payload: UpdateAppealStatusPayload) =>
      reportingAppealsApi.updateAppealStatus(payload),
    onSuccess: (response: any, _payload) => {
      const matterInvoiceQuery = queryClient.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];
      const invoice = response.data;

      const invoiceIndex = findIndex(
        matterInvoices,
        matterInvoice => matterInvoice.invoiceId === invoice.invoiceId
      );

      if (invoiceIndex !== -1) {
        const collectedAmount = invoice.collectedAmount ?? 0;
        const paid = invoice.paid ?? 0;
        const arBalance = Math.round((collectedAmount - paid) * 100) / 100;

        set(matterInvoices, `[${invoiceIndex}]`, {
          ...matterInvoices[invoiceIndex],
          appealStatus: AppealStatus.NO_APPEAL,
          remainingAmount: invoice.remainingAmount,
          collectedAmount: invoice.collectedAmount,
          appealComments: invoice.comments,
          writeOffAmount: invoice.writeOffAmount,
          actualBalance: invoice.actualBalance,
          arBalance,
        });
      }

      queryClient.setQueryData(matterInvoicesQueryKey, {
        ...matterInvoiceQuery,
        data: matterInvoices,
      });

      // Invalidate the appeals and appeal by client id query keys
      const invalidateQueries = [appealsQueryKey, appealByClientIdQueryKey];
      invalidateQueries.forEach(queryKey => {
        if (!isEmpty(queryKey)) {
          queryClient.invalidateQueries({
            queryKey,
          });
        }
      });
      showToast.success(`The invoice has been marked as discount only successfully.`);
    },
    onError: error => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to create discount only entry. Please try again.')
      );
    },
  });
};

export const useMarkWriteOff = (invoiceId: string) => {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');
  return useMutation({
    mutationFn: (payload: MarkWriteOffPayload) => {
      const payloadTransform: UpdateAppealStatusPayload = {
        invoiceId,
        nextStatus: AppealStatus.NO_APPEAL,
        comment: payload.comment,
      };
      return reportingAppealsApi.updateAppealStatus(payloadTransform);
    },
    onSuccess: (response: any, _payload) => {
      const matterInvoiceQuery = queryClient.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];
      const invoice = response.data;

      const invoiceIndex = findIndex(
        matterInvoices,
        matterInvoice => matterInvoice.invoiceId === invoice.invoiceId
      );

      if (invoiceIndex !== -1) {
        const collectedAmount = invoice.collectedAmount ?? 0;
        const paid = invoice.paid ?? 0;
        const arBalance = Math.round((collectedAmount - paid) * 100) / 100;

        set(matterInvoices, `[${invoiceIndex}]`, {
          ...matterInvoices[invoiceIndex],
          appealStatus: AppealStatus.NO_APPEAL,
          remainingAmount: invoice.remainingAmount,
          collectedAmount: invoice.collectedAmount,
          appealComments: invoice.comments,
          writeOffAmount: invoice.writeOffAmount,
          actualBalance: invoice.actualBalance,
          arBalance,
        });
      }

      queryClient.setQueryData(matterInvoicesQueryKey, {
        ...matterInvoiceQuery,
        data: matterInvoices,
      });

      // Invalidate the appeals and appeal by client id query keys
      const invalidateQueries = [appealsQueryKey, appealByClientIdQueryKey];
      invalidateQueries.forEach(queryKey => {
        if (!isEmpty(queryKey)) {
          queryClient.invalidateQueries({
            queryKey,
          });
        }
      });
      showToast.success(`The write-off has been created successfully.`);
    },
    onError: error => {
      showToast.error(getApiErrorMessage(error, 'Failed to create write-off. Please try again.'));
    },
  });
};

export const useUploadMatterInvoiceSummaryFiles = (
  invoiceId: string,
  source = 'AR Collections',
  {
    isShowToast = true,
    category,
  }: {
    isShowToast?: boolean;
    category: FileCategory;
  } = {
    isShowToast: true,
    category: FileCategory.PAYMENT_SUPPORT,
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      files: File[];
      successCallback?: (response: {
        successFiles: MatterInvoiceSummaryFile[];
        errorFiles: File[];
      }) => void;
    }) =>
      arMatterApi.uploadMatterInvoiceSummaryFiles(invoiceId, {
        files: payload.files,
        source: source,
        category: category,
      }),
    onSuccess: (data, payload) => {
      if (data.successFiles.length === 0) {
        const errorFileNames = data.errorFiles.map(file => file.name).join(', ');
        const errorCount = data.errorFiles.length;
        const errorMessage =
          errorCount === 1
            ? `1 file failed to upload: ${errorFileNames}`
            : `All ${errorCount} files failed to upload: ${errorFileNames}`;
        showToast.error(errorMessage);
      } else if (
        data.successFiles.length > 0 &&
        data.successFiles.length !== payload.files.length
      ) {
        if (isShowToast) {
          showToast.success(`${data.successFiles.length} files uploaded successfully`);
        }

        const errorFileNames = data.errorFiles.map(file => file.name).join(', ');
        const errorCount = data.errorFiles.length;
        const errorMessage =
          errorCount === 1
            ? `1 file failed to upload: ${errorFileNames}`
            : `${errorCount} files failed to upload: ${errorFileNames}`;
        showToast.error(errorMessage);
      } else {
        if (isShowToast) {
          showToast.success('Files uploaded successfully');
        }
      }
      const cachedData = queryClient.getQueryData<ArMatterInvoiceSummaryFilesResponse>([
        'matter',
        'invoice',
        'summary-files',
        invoiceId,
      ]);
      const newData = {
        ...cachedData,
        data: [...data.successFiles.reverse(), ...(cachedData?.data || [])],
      };

      queryClient.setQueryData(['matter', 'invoice', 'summary-files', invoiceId], newData);
      payload?.successCallback?.({ successFiles: data.successFiles, errorFiles: data.errorFiles });
    },
  });
};

export const useDeleteMatterInvoiceSummaryFile = (invoiceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { fileId: string; successCallback?: () => void }) =>
      arMatterApi.deleteMatterInvoiceSummaryFile(invoiceId, payload.fileId),
    onSuccess: (_, payload) => {
      showToast.success('File deleted successfully');
      const cachedData = queryClient.getQueryData<ArMatterInvoiceSummaryFilesResponse>([
        'matter',
        'invoice',
        'summary-files',
        invoiceId,
      ]);
      const newData = {
        ...cachedData,
        data: (cachedData?.data || []).filter(file => file.fileId !== payload.fileId),
      };
      queryClient.setQueryData(['matter', 'invoice', 'summary-files', invoiceId], newData);
      payload?.successCallback?.();
    },
    onError: error => {
      showToast.error(getApiErrorMessage(error, 'Failed to delete file. Please try again.'));
    },
  });
};

export const useCaptureArMatterData = (
  arMatterData: ArMatter,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [`/capture/matters/${arMatterData?.matterId}`] as const,
    queryFn: () => arMatterApi.captureArMatterData(arMatterData),
    enabled: options.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
};
